import type { PdfPageBox } from '@/pages/document/DocumentViewer/DocumentFileView/pageGeometry';

// The stamp is a fifth of the page height, with the proportions of its image.
const PAGE_HEIGHT_TO_STAMP_HEIGHT = 5;
const STAMP_IMAGE_WIDTH = 254;
const STAMP_IMAGE_HEIGHT = 474;
const STAMP_MARGIN = 5;

/** A size in screen pixels, as the page or the stamp is shown. */
export type DisplayedSize = { width: number; height: number };

/**
 * The stamp's offset from its anchor in screen pixels. The anchor is the page's top-left corner, or its
 * top-right corner in a right-to-left language, so `x` is then zero or negative.
 */
export type DisplayedApprovalStampOffset = { x: number; y: number };

/** The stamp's lower-left and upper-right corners in the units of the PDF page box, `y` counted from the bottom. */
export type ApprovalStampCorners = { ll: [number, number]; ur: [number, number] };

// The lower bound wins when the range is empty.
const clamp = (value: number, min: number, max: number) => (value < min ? min : Math.min(value, max));

const roundToTenth = (value: number) => Math.round((value + Number.EPSILON) * 10) / 10;

export const approvalStampSize = (page: DisplayedSize): DisplayedSize => {
  const height = page.height / PAGE_HEIGHT_TO_STAMP_HEIGHT;
  return { width: height * (STAMP_IMAGE_WIDTH / STAMP_IMAGE_HEIGHT), height };
};

export const defaultApprovalStampOffset = (
  page: DisplayedSize,
  stamp: DisplayedSize,
  isRtl: boolean,
): DisplayedApprovalStampOffset => ({
  x: isRtl ? -STAMP_MARGIN : page.width - stamp.width - STAMP_MARGIN,
  y: page.height - stamp.height - STAMP_MARGIN,
});

export const clampApprovalStampOffset = (
  { x, y }: DisplayedApprovalStampOffset,
  page: DisplayedSize,
  stamp: DisplayedSize,
  isRtl: boolean,
): DisplayedApprovalStampOffset => {
  const minX = isRtl ? -(page.width - stamp.width) : 0;
  const maxX = isRtl ? 0 : page.width - stamp.width;
  return {
    x: clamp(x, minX, maxX),
    y: clamp(y, 0, page.height - stamp.height),
  };
};

// The ratio of the half-perimeters is the same whichever way the page is turned, so the box from the file
// needs no swap for a rotated page.
export const approvalStampCorners = (
  { x, y }: DisplayedApprovalStampOffset,
  page: DisplayedSize,
  stamp: DisplayedSize,
  pdfPageBox: PdfPageBox,
  isRtl: boolean,
): ApprovalStampCorners => {
  const pixelsPerBoxUnit = (page.width + page.height) / (pdfPageBox.width + pdfPageBox.height);
  const toBoxUnits = (value: number) => roundToTenth(value / pixelsPerBoxUnit);
  const left = isRtl ? page.width - stamp.width + x : x;
  const bottom = page.height - y - stamp.height;
  return {
    ll: [toBoxUnits(left), toBoxUnits(bottom)],
    ur: [toBoxUnits(left + stamp.width), toBoxUnits(page.height - y)],
  };
};
